import { clipPathToSolid, pointInSolid, type SolidClip } from './collision.ts';
import {
  CRASH_BOUNCE_MAX,
  CRASH_BOUNCE_MIN_PX,
  CRASH_FILLET_MISS_PX,
  CRASH_FILLET_R,
  CRASH_HARSHNESS,
  MIN_SPEED,
} from './constants.ts';
import { crashOutgoingVelocity } from './crash.ts';
import type { MapDocument } from './map/types.ts';
import { add, angleDelta, fromAngle, length, sub, toAngle } from './math.ts';
import {
  circularArcSegments,
  concatMovePaths,
  pathFromPolyline,
  polylineLength,
  sampleMovePath,
  trimMovePathEnd,
  type CurveKind,
  type MovePath,
} from './path.ts';
import type { BoatState, Vec2 } from './types.ts';

export type BounceResult = {
  boat: BoatState;
  path: MovePath;
};

const FILLET_MIN_TURN = 0.08;

/**
 * Spend leftover crash speed as outbound casts after the first solid clip.
 * Each open-water cast lands; each new solid applies `crashOutgoingVelocity`
 * again until the path is clear or `CRASH_BOUNCE_MAX` outbounds are used.
 */
export function resolveBounceChain(
  map: MapDocument,
  clip: SolidClip,
  inboundTravel: number,
  harshness: number = CRASH_HARSHNESS,
): BounceResult {
  const kind = clip.path.kind;
  const first = crashOutgoingVelocity(
    clip.approach,
    sub(clip.edge.to, clip.edge.from),
    inboundTravel,
    harshness,
  );

  let path = clip.path;
  let current = clip.point;
  let approach = clip.approach;
  let leftover = first?.speed ?? inboundTravel;
  let heading = first?.heading ?? toAngle(clip.approach);

  for (let attempt = 0; attempt < CRASH_BOUNCE_MAX; attempt += 1) {
    if (leftover < CRASH_BOUNCE_MIN_PX) {
      break;
    }

    const inboundHeading = toAngle(approach);
    const fillet = bounceFillet(map, path, current, inboundHeading, heading, leftover, kind);
    const inbound = fillet?.inbound ?? path;
    const start = fillet?.end ?? current;
    const remaining = Math.max(0, leftover - (fillet?.arcLength ?? 0));
    const dest =
      remaining > 1e-6 ? add(start, fromAngle(heading, remaining)) : { ...start };
    const outbound =
      length(sub(dest, start)) > 1e-6 ? pathFromPolyline([start, dest], kind) : null;
    const nextClip =
      outbound && !pointInSolid(map, start) ? clipPathToSolid(map, outbound, start) : null;
    const parts = fillet ? [inbound, fillet.path] : [inbound];

    if (!nextClip) {
      if (outbound && !pointInSolid(map, dest)) {
        parts.push(outbound);

        return {
          path: concatMovePaths(kind, parts) ?? path,
          boat: {
            position: dest,
            heading,
            speed: leftover,
          },
        };
      }

      break;
    }

    parts.push(nextClip.path);
    path = concatMovePaths(kind, parts) ?? path;
    current = nextClip.point;
    approach = nextClip.approach;

    const next = crashOutgoingVelocity(
      approach,
      sub(nextClip.edge.to, nextClip.edge.from),
      leftover,
      harshness,
    );

    leftover = next?.speed ?? leftover;
    heading = next?.heading ?? heading;

    if (attempt === CRASH_BOUNCE_MAX - 1) {
      break;
    }
  }

  return {
    path,
    boat: {
      position: current,
      heading,
      speed: Math.max(MIN_SPEED, leftover),
    },
  };
}

function bounceFillet(
  map: MapDocument,
  inboundPath: MovePath,
  corner: Vec2,
  inboundHeading: number,
  outboundHeading: number,
  leftover: number,
  kind: CurveKind,
): { inbound: MovePath; path: MovePath; end: Vec2; arcLength: number } | null {
  const sweep = Math.abs(angleDelta(inboundHeading, outboundHeading));

  if (sweep < FILLET_MIN_TURN || leftover < CRASH_BOUNCE_MIN_PX) {
    return null;
  }

  const tanHalf = Math.tan(sweep / 2);
  const secHalf = 1 / Math.cos(sweep / 2);

  if (!Number.isFinite(tanHalf) || !Number.isFinite(secHalf) || tanHalf < 1e-6) {
    return null;
  }

  const inboundLen = polylineLength(sampleMovePath(inboundPath));
  const radius = Math.min(
    CRASH_FILLET_R,
    leftover / 2,
    (leftover * 0.85) / sweep,
    (inboundLen * 0.85) / tanHalf,
    CRASH_FILLET_MISS_PX / Math.max(secHalf - 1, 1e-6),
  );

  if (radius < 1) {
    return null;
  }

  const tangent = radius * tanHalf;
  const inbound = trimMovePathEnd(inboundPath, tangent);
  const start = inbound?.segments.at(-1)?.p3;

  if (!inbound || !start) {
    return null;
  }

  const end = add(corner, fromAngle(outboundHeading, tangent));

  if (pointInSolid(map, end)) {
    return null;
  }

  const segments = circularArcSegments(start, inboundHeading, end);

  if (segments.length === 0) {
    return null;
  }

  const fillet = { kind, segments };
  const samples = sampleMovePath(fillet);
  const mid = samples.at(Math.floor(samples.length / 2));

  if (mid && pointInSolid(map, mid)) {
    return null;
  }

  return {
    inbound,
    path: fillet,
    end,
    arcLength: radius * sweep,
  };
}
